import React, { FC, useCallback, useMemo } from 'react'
import { FlexColumn } from '@/atoms/FlexContainers'
import { HeadingLG, TitleLG, TitleMD } from '@/atoms/Text'
import { usePayItForwardOfferData } from '@/contexts/PayItForwardOffersDataContext'
import { useThemeContext } from '@/contexts/ThemeContext'
import { Offer } from '@/services/PifService'
import { useTranslate } from '@/utils/translate/translate-client'
import { ExpressOffer } from '@/views/ExpressCheckoutView/components/ExpressOffer'
import { usePifOfferTickets } from '@/views/ExpressCheckoutView/components/useOfferTickets'

interface Props {
  onOfferSelect?: (offerId: string) => Promise<void>
}

const ExpressOffers: FC<Props> = ({ onOfferSelect }) => {
  const { t } = useTranslate('pif')
  const { isDarkMode } = useThemeContext()
  const { offersData, selectedOffer, handleSetSelectedOfferId } = usePayItForwardOfferData()
  const { formattedNumberOfTickets } = usePifOfferTickets({ offer: selectedOffer })

  const filteredOffers = useMemo(() => {
    return offersData?.filter((offer) => offer?.price <= 1500000)
  }, [offersData])

  const handleOfferClick = useCallback(
    async (offer: Offer) => {
      if (onOfferSelect) {
        await onOfferSelect(offer.id)
      }

      handleSetSelectedOfferId(offer.id)
    },
    [handleSetSelectedOfferId, onOfferSelect],
  )

  const headerContent = useMemo(() => {
    if (!selectedOffer) return null

    return (
      <FlexColumn className="items-center text-center">
        <HeadingLG color={isDarkMode ? 'white' : 'core-gray-950'} weight="bold">
          {formattedNumberOfTickets}
        </HeadingLG>
        <TitleMD weight="medium" color={isDarkMode ? 'core-gray-500' : 'core-gray-600'}>
          {t('ticketsSelected', 'Tickets Selected')}
        </TitleMD>

        <TitleLG className="mt-6" weight="semibold" color={isDarkMode ? 'white' : 'core-gray-950'}>
          {t('numberTicketsSelected', '{{numberOfTickets}} Tickets Selected', {
            numberOfTickets: formattedNumberOfTickets,
          })}
        </TitleLG>
      </FlexColumn>
    )
  }, [isDarkMode, selectedOffer, formattedNumberOfTickets, t])

  if (!selectedOffer) return null

  return (
    <FlexColumn className="gap-10">
      {headerContent}
      <FlexColumn className="gap-4 @container">
        <div className="grid grid-cols-3 gap-2 @xs:grid-cols-4 @sm:flex @sm:flex-wrap">
          {filteredOffers?.map((offer) => {
            if (!offer) return null
            return (
              <ExpressOffer
                onClick={() => handleOfferClick(offer)}
                isSelected={selectedOffer?.id === offer.id}
                offer={offer}
                key={offer.id}
              />
            )
          })}
        </div>
      </FlexColumn>
    </FlexColumn>
  )
}

export default ExpressOffers
